import includes from "lodash/includes";
import pick from "lodash/pick";
import sortBy from "lodash/sortBy";
import { handleActions } from "redux-actions";

import { dealsFilter, dealSort, nextSortDir, removeById, updateById } from "utils";
import activityLevelColor from "utils/activityFeed/activityLevelColor";

import { defaultState } from "../configureStore";

const formatDeal = (deal, companyType) => ({
  ...deal,
  activityLevelColor: activityLevelColor(deal, companyType),
  assets: sortBy(deal.assets || [], "name"),
});

const withDefaultProps = (deal, company) => ({
  ...deal,
  budgetPeriod: deal.budgetPeriod || company.period,
  budget: deal.budget || { currency: company.currency },
  size: deal.size || { unit: company.statsUnit },
  term: deal.term || {
    period: company.period === "monthly" ? "monthly" : "yearly",
  },
});

const dealReducer = handleActions(
  {
    DEALS_FILTER: ({ deals }, { meta: { filters } }) => ({
      filteredDeals: dealsFilter(deals, filters),
    }),

    DEALS_GET: {
      PENDING: () => ({
        deals: [],
        filteredDeals: [],
      }),
      FULFILLED: (_state, { payload: { deals } }) => ({
        deals: deals.map((deal) => formatDeal(deal, deal.portfolio.company)),
      }),
    },

    DEALS_SORT: (state, action) => {
      const sortDir =
        action.payload.key === state.dealsSortKey ? nextSortDir(state.dealsSortDir) : "asc";

      return {
        dealsSortDir: sortDir,
        dealsSortKey: action.payload.key,
        deals: dealSort(state.deals, action.payload.key, sortDir),
        filteredDeals: dealSort(state.filteredDeals, action.payload.key, sortDir),
      };
    },

    DEAL_CREATE: {
      FULFILLED: (state, action) => ({
        deals: [...state.deals, action.payload.deal],
      }),
    },

    DEAL_GET: {
      PENDING: () => ({
        posts: defaultState.posts,
        deals: defaultState.deals,
        deal: defaultState.deal,
        channels: defaultState.channels,
        tenant: defaultState.tenant,
        tenantContacts: defaultState.tenantContacts,
        proposals: defaultState.proposals,
      }),
      FULFILLED: (state, action) => {
        const { deal } = action.payload;

        return {
          deal: {
            ...withDefaultProps(deal, state.currentUser.company),
            assets: deal.assets || [],
          },
        };
      },
    },

    DEAL_UPDATE_ASSETS: (state, action) => {
      const assets = sortBy(
        state.assetNames.filter((asset) => includes(action.meta.assetIDs, asset.id)),
        "name"
      );

      return {
        deal: {
          ...state.deal,
          assets,
        },
      };
    },

    DEAL_SAVE: {
      FULFILLED: (state, action) => ({
        deal: withDefaultProps(action.payload.deal, state.currentUser.company),
      }),
    },

    DEAL_DELETE: {
      FULFILLED: (state, action) => ({
        deal: defaultState.deal,
        deals: removeById(state.deals, action.meta.dealID),
        filteredDeals: removeById(state.filteredDeals, action.meta.dealID),
        posts: defaultState.posts,
      }),
    },

    DEAL_EXECUTE: {
      FULFILLED: (state, action) => ({
        deal: withDefaultProps(action.payload.deal, state.currentUser.company),
        proposals: updateById(state.proposals, {
          ...state.proposals.find((p) => p.id === action.payload.deal.executedProposalId),
          executed: true,
        }),
        posts: [action.payload.deal.lastItem, ...state.posts],
      }),
    },

    DEAL_REOPEN: {
      FULFILLED: (state, action) => ({
        deal: withDefaultProps(action.payload.deal, state.currentUser.company),
        proposals: updateById(state.proposals, {
          ...state.proposals.find((p) => p.executed),
          executed: false,
        }),
        posts: [action.payload.deal.lastItem, ...state.posts],
      }),
    },

    DEAL_SUBSCRIBE: {
      FULFILLED: (state) => ({
        deal: { ...state.deal, subscribed: true },
      }),
    },

    DEAL_UNSUBSCRIBE: {
      FULFILLED: (state) => ({
        deal: { ...state.deal, subscribed: false },
      }),
    },

    DEAL_DISREGARD: {
      FULFILLED: (state, action) => ({
        deal: withDefaultProps(action.payload.deal, state.currentUser.company),
        posts: [action.payload.deal.lastItem, ...state.posts],
      }),
    },

    DEAL_CANCEL_FULFILLED: (state, action) => ({
      deal: withDefaultProps(action.payload.deal, state.currentUser.company),
      posts: [action.payload.deal.lastItem, ...state.posts],
      proposals: state.proposals.map((proposal) => ({
        ...proposal,
        executed: false,
      })),
    }),

    DEAL_UPDATE_STAGE: {
      PENDING: ({ deals, filteredDeals }, action) => ({
        deals: updateById(deals, {
          id: action.meta.dealID,
          stage: action.meta.stage,
        }),
        filteredDeals: updateById(filteredDeals, {
          id: action.meta.dealID,
          stage: action.meta.stage,
        }),
      }),
      FULFILLED: (state, { payload: { deal } }) => ({
        // TODO: deal stage assets?
        deal: withDefaultProps(deal, state.currentUser.company),
        deals: updateById(
          state.deals,
          formatDeal(pick(deal, ["updatedAt", "id", "lastItem", "stage", "portfolio"]))
        ),
        filteredDeals: updateById(
          state.filteredDeals,
          formatDeal(pick(deal, ["updatedAt", "id", "lastItem", "stage", "portfolio"]))
        ),
        posts: [deal.lastItem, ...state.posts],
      }),
    },

    DEAL_UPDATE_POINT_PERSON: {
      FULFILLED: (state, action) => ({
        deal: { ...state.deal, pointPerson: action.payload.deal.pointPerson },
      }),
    },

    DEAL_UPDATE_SHARE_STATUS: {
      FULFILLED: (state, action) => ({
        deal: {
          ...state.deal,
          shareStatus: action.payload.deal.shareStatus,
          sharedBrokers: action.payload.deal.sharedBrokers,
          collaborators: action.payload.deal.collaborators,
        },
        posts: [action.payload.deal.lastItem, ...state.posts],
      }),
    },
  },
  {},
  { namespace: "_" }
);

export default dealReducer;
